import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shuffle, Zap, HelpCircle, Trophy, RefreshCw, Timer, Star, Volume2 } from 'lucide-react';
import { useAuth, API } from '../App';
import { useTTS } from '../hooks/useTTS';
import Navbar from '../components/Navbar';
import axios from 'axios';

// Word pools by difficulty
const EASY_WORDS = ['cat', 'dog', 'sun', 'hat', 'run', 'big', 'red', 'fun', 'cup', 'bed', 'hot', 'pen', 'sit', 'top', 'box', 'fox', 'map', 'bus', 'bug', 'nut'];
const MEDIUM_WORDS = ['tree', 'fish', 'jump', 'play', 'book', 'frog', 'ship', 'star', 'drum', 'cake', 'snow', 'rain', 'duck', 'lamp', 'ring', 'ball', 'sand', 'bird', 'milk', 'door'];
const HARD_WORDS = ['cloud', 'house', 'tiger', 'river', 'beach', 'plant', 'dance', 'space', 'train', 'smile', 'round', 'light', 'chair', 'write', 'brown', 'green', 'bread', 'sleep', 'friend', 'story'];

function shuffleWord(word) {
  const arr = word.split('');
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  const shuffled = arr.join('');
  return shuffled === word ? shuffleWord(word) : shuffled;
}

function pickRandom(arr, count) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// ========== WORD SCRAMBLE ==========
const WordScramble = ({ onScore, voiceEnabled, playerVoice }) => {
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const inputRef = useRef(null);
  const { speakAsPlayer, stop: stopTTS, isPlaying } = useTTS();

  useEffect(() => {
    const allWords = [...pickRandom(EASY_WORDS, 4), ...pickRandom(MEDIUM_WORDS, 3), ...pickRandom(HARD_WORDS, 3)];
    setWords(allWords.map(w => ({ word: w, scrambled: shuffleWord(w) })));
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const current = words[currentIndex];

  const checkAnswer = () => {
    if (!current) return;
    const correct = input.toLowerCase().trim() === current.word;
    if (correct) {
      const points = showHint ? 5 : 10;
      setScore(prev => prev + points);
      setFeedback({ correct: true, message: `+${points} points!` });
    } else {
      setFeedback({ correct: false, message: `It was "${current.word}"` });
    }
    setShowHint(false);
    setTimeout(() => {
      setFeedback(null);
      setInput('');
      if (currentIndex + 1 >= words.length) {
        const finalScore = score + (correct ? (showHint ? 5 : 10) : 0);
        setGameOver(true);
        onScore(finalScore);
      } else {
        setCurrentIndex(prev => prev + 1);
        inputRef.current?.focus();
      }
    }, 1200);
  };

  const useHint = () => {
    setShowHint(true);
    setHintsUsed(prev => prev + 1);
  };

  const readAloud = () => {
    if (current && voiceEnabled) speakAsPlayer(current.word, playerVoice);
  };

  if (gameOver) {
    return (
      <div className="text-center py-8" data-testid="scramble-results">
        <Trophy className="w-16 h-16 text-sunny-yellow mx-auto mb-4" />
        <h3 className="font-display text-3xl text-text-dark mb-2">Word Scramble Complete!</h3>
        <p className="font-display text-4xl text-mint-green mb-2">{score} points</p>
        <p className="text-text-medium text-sm">Hints used: {hintsUsed}</p>
      </div>
    );
  }

  if (!current) return null;

  return (
    <div className="space-y-6" data-testid="scramble-game">
      <div className="flex justify-between items-center">
        <span className="text-text-medium font-body text-sm">{currentIndex + 1} / {words.length}</span>
        <span className="font-display text-xl text-sunny-yellow">{score} pts</span>
      </div>
      <div className="bg-gradient-to-r from-cream to-warm-white rounded-2xl p-8 text-center border-2 border-sunny-yellow/30">
        <p className="text-text-medium text-sm mb-2 font-semibold">UNSCRAMBLE THIS WORD</p>
        <p className="font-mono text-4xl tracking-[0.3em] text-text-dark font-bold">{current.scrambled.toUpperCase()}</p>
        {showHint && (
          <p className="mt-3 text-sunny-yellow font-body text-sm">
            Hint: First letter is "<span className="font-bold">{current.word[0].toUpperCase()}</span>", last is "<span className="font-bold">{current.word[current.word.length - 1].toUpperCase()}</span>"
          </p>
        )}
      </div>
      {feedback && (
        <div className={`text-center py-3 rounded-xl font-display text-lg ${feedback.correct ? 'bg-mint-green/20 text-mint-green' : 'bg-coral-orange/20 text-coral-orange'}`}>
          {feedback.message}
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) checkAnswer(); }}
          className="flex-1 px-4 py-3 bg-white border-2 border-soft-gray/50 rounded-xl text-text-dark font-mono text-xl text-center focus:outline-none focus:border-mint-green focus:ring-2 focus:ring-mint-green/20"
          placeholder="Type the word..."
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          data-testid="scramble-input"
        />
        {voiceEnabled && (
          <button onClick={readAloud} disabled={isPlaying} className="px-3 rounded-xl bg-lavender/20 text-lavender hover:bg-lavender/30" data-testid="scramble-listen">
            <Volume2 className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="flex gap-3">
        <button onClick={checkAnswer} disabled={!input.trim() || feedback} className="flex-1 py-3 bg-gradient-to-r from-mint-green to-sky-blue text-white font-display text-lg rounded-xl disabled:opacity-50">
          Check
        </button>
        <button onClick={useHint} disabled={showHint || feedback} className="px-4 py-3 bg-sunny-yellow/20 text-sunny-yellow rounded-xl font-display disabled:opacity-30">
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ========== SPEED ROUND ==========
const SpeedRound = ({ onScore, voiceEnabled, playerVoice }) => {
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const pool = [...EASY_WORDS, ...MEDIUM_WORDS];
    setWords(pool.sort(() => Math.random() - 0.5));
  }, []);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    if (timeLeft <= 0) {
      setGameOver(true);
      onScore(score);
      return;
    }
    const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, gameStarted, gameOver]);

  const startGame = () => {
    setGameStarted(true);
    setTimeLeft(30);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleInput = (e) => {
    const value = e.target.value;
    setInput(value);
    const currentWord = words[currentIndex % words.length];
    if (value.toLowerCase().trim() === currentWord) {
      const points = currentWord.length >= 5 ? 15 : 10;
      setScore(prev => prev + points);
      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak > bestStreak) setBestStreak(newStreak);
      setInput('');
      setCurrentIndex(prev => prev + 1);
    }
  };

  if (gameOver) {
    return (
      <div className="text-center py-8" data-testid="speed-results">
        <Zap className="w-16 h-16 text-sunny-yellow mx-auto mb-4" />
        <h3 className="font-display text-3xl text-text-dark mb-2">Time's Up!</h3>
        <p className="font-display text-4xl text-mint-green mb-2">{score} points</p>
        <p className="text-text-medium text-sm">{currentIndex} words typed &middot; Best streak: {bestStreak}</p>
      </div>
    );
  }

  if (!gameStarted) {
    return (
      <div className="text-center py-8" data-testid="speed-start">
        <Zap className="w-16 h-16 text-coral-orange mx-auto mb-4" />
        <h3 className="font-display text-2xl text-text-dark mb-2">Speed Round</h3>
        <p className="text-text-medium mb-6">Type as many words as you can in 30 seconds!</p>
        <button onClick={startGame} className="px-10 py-4 bg-gradient-to-r from-coral-orange to-sunny-yellow text-white font-display text-xl rounded-xl shadow-lg hover:scale-105 transition-transform" data-testid="speed-start-btn">
          GO!
        </button>
      </div>
    );
  }

  const currentWord = words[currentIndex % words.length];

  return (
    <div className="space-y-6" data-testid="speed-game">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Timer className={`w-5 h-5 ${timeLeft <= 10 ? 'text-coral-orange animate-pulse' : 'text-text-medium'}`} />
          <span className={`font-display text-2xl ${timeLeft <= 10 ? 'text-coral-orange' : 'text-text-dark'}`}>{timeLeft}s</span>
        </div>
        <span className="font-display text-xl text-sunny-yellow">{score} pts</span>
        <div className="flex items-center gap-1">
          <Star className="w-4 h-4 text-coral-orange" />
          <span className="text-sm text-text-medium">x{streak}</span>
        </div>
      </div>
      <div className="bg-gradient-to-r from-cream to-warm-white rounded-2xl p-8 text-center border-2 border-coral-orange/30">
        <p className="font-mono text-4xl text-text-dark font-bold">{currentWord}</p>
      </div>
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={handleInput}
        className="w-full px-4 py-4 bg-white border-2 border-soft-gray/50 rounded-xl text-text-dark font-mono text-xl text-center focus:outline-none focus:border-coral-orange focus:ring-2 focus:ring-coral-orange/20"
        placeholder="Type it!"
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
        data-testid="speed-input"
      />
      <div className="h-2 bg-soft-gray rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-coral-orange to-sunny-yellow transition-all" style={{ width: `${(timeLeft / 30) * 100}%` }} />
      </div>
    </div>
  );
};

// ========== MISSING LETTERS ==========
const MissingLetters = ({ onScore, voiceEnabled, playerVoice }) => {
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const inputRef = useRef(null);
  const { speakAsPlayer, stop: stopTTS, isPlaying } = useTTS();

  useEffect(() => {
    const pool = [...pickRandom(EASY_WORDS, 4), ...pickRandom(MEDIUM_WORDS, 3), ...pickRandom(HARD_WORDS, 3)];
    const prepared = pool.map(word => {
      const letters = word.split('');
      const hideCount = Math.max(1, Math.floor(letters.length * 0.4));
      const indices = [];
      while (indices.length < hideCount) {
        const idx = Math.floor(Math.random() * letters.length);
        if (!indices.includes(idx)) indices.push(idx);
      }
      const display = letters.map((l, i) => indices.includes(i) ? '_' : l).join('');
      return { word, display, missing: indices };
    });
    setWords(prepared);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const current = words[currentIndex];

  const checkAnswer = () => {
    if (!current) return;
    const correct = input.toLowerCase().trim() === current.word;
    const points = correct ? 10 : 0;
    if (correct) setScore(prev => prev + points);
    setFeedback({ correct, message: correct ? `+${points} points!` : `It was "${current.word}"` });
    setTimeout(() => {
      setFeedback(null);
      setInput('');
      if (currentIndex + 1 >= words.length) {
        const finalScore = score + points;
        setGameOver(true);
        onScore(finalScore);
      } else {
        setCurrentIndex(prev => prev + 1);
        inputRef.current?.focus();
      }
    }, 1200);
  };

  const readAloud = () => {
    if (current && voiceEnabled) speakAsPlayer(current.word, playerVoice);
  };

  if (gameOver) {
    return (
      <div className="text-center py-8" data-testid="missing-results">
        <Star className="w-16 h-16 text-sunny-yellow mx-auto mb-4" />
        <h3 className="font-display text-3xl text-text-dark mb-2">Missing Letters Complete!</h3>
        <p className="font-display text-4xl text-mint-green mb-2">{score} points</p>
      </div>
    );
  }

  if (!current) return null;

  return (
    <div className="space-y-6" data-testid="missing-game">
      <div className="flex justify-between items-center">
        <span className="text-text-medium font-body text-sm">{currentIndex + 1} / {words.length}</span>
        <span className="font-display text-xl text-sunny-yellow">{score} pts</span>
      </div>
      <div className="bg-gradient-to-r from-cream to-warm-white rounded-2xl p-8 text-center border-2 border-lavender/30">
        <p className="text-text-medium text-sm mb-2 font-semibold">FILL IN THE MISSING LETTERS</p>
        <p className="font-mono text-4xl tracking-[0.3em] text-text-dark font-bold">
          {current.display.split('').map((c, i) => (
            <span key={i} className={c === '_' ? 'text-coral-orange' : 'text-text-dark'}>{c === '_' ? '_' : c.toUpperCase()}</span>
          ))}
        </p>
      </div>
      {feedback && (
        <div className={`text-center py-3 rounded-xl font-display text-lg ${feedback.correct ? 'bg-mint-green/20 text-mint-green' : 'bg-coral-orange/20 text-coral-orange'}`}>
          {feedback.message}
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) checkAnswer(); }}
          className="flex-1 px-4 py-3 bg-white border-2 border-soft-gray/50 rounded-xl text-text-dark font-mono text-xl text-center focus:outline-none focus:border-lavender focus:ring-2 focus:ring-lavender/20"
          placeholder="Type the full word..."
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          data-testid="missing-input"
        />
        {voiceEnabled && (
          <button onClick={readAloud} disabled={isPlaying} className="px-3 rounded-xl bg-lavender/20 text-lavender hover:bg-lavender/30" data-testid="missing-listen">
            <Volume2 className="w-5 h-5" />
          </button>
        )}
      </div>
      <button onClick={checkAnswer} disabled={!input.trim() || feedback} className="w-full py-3 bg-gradient-to-r from-lavender to-purple-500 text-white font-display text-lg rounded-xl disabled:opacity-50">
        Check
      </button>
    </div>
  );
};

// ========== MAIN MINI GAMES PAGE ==========
const GAMES = [
  { id: 'scramble', name: 'Word Scramble', icon: Shuffle, color: 'sunny-yellow', desc: 'Unscramble jumbled letters', Component: WordScramble },
  { id: 'speed', name: 'Speed Round', icon: Zap, color: 'coral-orange', desc: 'Type fast - 30 second blitz!', Component: SpeedRound },
  { id: 'missing', name: 'Missing Letters', icon: HelpCircle, color: 'lavender', desc: 'Fill in the blanks', Component: MissingLetters },
];

const MiniGamesPage = () => {
  const { user, refreshUser } = useAuth();
  const [selectedGame, setSelectedGame] = useState(null);
  const [finalScore, setFinalScore] = useState(null);
  const [coinsEarned, setCoinsEarned] = useState(0);

  const voiceEnabled = user?.voice_enabled !== false;
  const playerVoice = user?.character_voice || 'alice';

  const handleScore = async (score) => {
    setFinalScore(score);
    const coins = Math.max(1, Math.floor(score / 10));
    setCoinsEarned(coins);
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/typing/session`, {
        phrase: `mini-game:${selectedGame}`,
        typed_text: `score:${score}`,
        keystrokes: score,
        correct_keystrokes: score,
        duration_seconds: 30,
        is_challenge: false,
        is_duel: false,
      }, { headers: { Authorization: `Bearer ${token}` } });
      refreshUser();
    } catch (e) {
      console.error('Failed to save mini-game score:', e);
    }
  };

  const resetGame = () => {
    setSelectedGame(null);
    setFinalScore(null);
    setCoinsEarned(0);
  };

  const ActiveGame = selectedGame ? GAMES.find(g => g.id === selectedGame)?.Component : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white">
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-20">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard" className="w-10 h-10 bg-white border-2 border-soft-gray/30 rounded-xl flex items-center justify-center hover:bg-soft-gray/20 transition-colors" data-testid="minigames-back">
            <ArrowLeft className="w-5 h-5 text-text-medium" />
          </Link>
          <div>
            <h1 className="font-display text-2xl text-text-dark">Mini Games</h1>
            <p className="text-text-medium text-sm">Fun ways to practise typing!</p>
          </div>
          {user && (
            <div className="ml-auto bg-sunny-yellow/20 rounded-xl px-3 py-1.5 border border-sunny-yellow/30">
              <span className="font-display text-lg text-sunny-yellow">{user.coins || 0} coins</span>
            </div>
          )}
        </div>

        {!selectedGame ? (
          /* Game Selection */
          <div className="grid gap-4" data-testid="minigames-list">
            {GAMES.map(game => (
              <button
                key={game.id}
                onClick={() => setSelectedGame(game.id)}
                className="bg-white border-2 border-soft-gray/30 rounded-2xl p-6 text-left hover:border-mint-green/50 hover:shadow-md transition-all group"
                data-testid={`minigame-${game.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 bg-${game.color}/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <game.icon className={`w-7 h-7 text-${game.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl text-text-dark">{game.name}</h3>
                    <p className="text-text-medium text-sm font-body">{game.desc}</p>
                  </div>
                  <ArrowLeft className="w-5 h-5 text-text-medium/30 rotate-180 group-hover:text-mint-green transition-colors" />
                </div>
              </button>
            ))}
          </div>
        ) : (
          /* Active Game */
          <div className="bg-white border-2 border-soft-gray/30 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-text-dark">
                {GAMES.find(g => g.id === selectedGame)?.name}
              </h2>
              <button onClick={resetGame} className="text-text-medium hover:text-coral-orange text-sm font-body flex items-center gap-1" data-testid="minigame-exit">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </div>

            {finalScore !== null ? (
              <div className="text-center py-4">
                {ActiveGame && <ActiveGame onScore={() => {}} voiceEnabled={voiceEnabled} playerVoice={playerVoice} />}
                <div className="mt-6 bg-sunny-yellow/20 rounded-xl p-4 border border-sunny-yellow/30">
                  <p className="text-text-medium text-sm">You earned</p>
                  <p className="font-display text-2xl text-sunny-yellow">+{coinsEarned} coins</p>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => { setFinalScore(null); setCoinsEarned(0); }}
                    className="flex-1 py-3 bg-gradient-to-r from-mint-green to-sky-blue text-white font-display text-lg rounded-xl flex items-center justify-center gap-2"
                    data-testid="minigame-replay"
                  >
                    <RefreshCw className="w-5 h-5" /> Play Again
                  </button>
                  <button
                    onClick={resetGame}
                    className="px-6 py-3 bg-soft-gray/30 text-text-medium font-display rounded-xl"
                    data-testid="minigame-back-list"
                  >
                    Games
                  </button>
                </div>
              </div>
            ) : (
              ActiveGame && <ActiveGame onScore={handleScore} voiceEnabled={voiceEnabled} playerVoice={playerVoice} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniGamesPage;
